// -----------------------------------------------------------------------------
// MySchoolMate — Deterministic School Match engine (Phase 1 demo)
//
// This module contains NO UI and NO external API calls. It parses a natural
// language request + optional filters into a structured `criteria` object, then
// scores each school. It is intentionally isolated so a real AI backend can
// replace `runMatch()` later without touching any component.
// -----------------------------------------------------------------------------

import { CITIES } from "../data/schools";

// Weighting per Phase 1 spec.
export const WEIGHTS = {
  budget: 0.25,
  location: 0.2,
  curriculum: 0.2,
  interest: 0.15,
  facilities: 0.1,
  ratio: 0.05,
  specialNeeds: 0.05,
};

const CURRICULUM_KEYWORDS = [
  { keys: ["islam", "islami", "iit"], value: "Islam Terpadu" },
  { keys: ["cambridge"], value: "Cambridge" },
  { keys: ["montessori"], value: "Montessori" },
  { keys: ["ib", "international baccalaureate", "internasional"], value: "IB" },
  { keys: ["stem", "sains terapan"], value: "STEM" },
  { keys: ["nasional plus", "nasional+"], value: "Nasional+" },
  { keys: ["nasional"], value: "Nasional" },
];

const INTEREST_KEYWORDS = [
  { keys: ["coding", "koding", "programming", "teknologi", "robotik", "robot"], value: "Coding" },
  { keys: ["olahraga", "sport", "sepak bola", "futsal", "basket", "renang", "berenang"], value: "Olahraga" },
  { keys: ["seni", "musik", "melukis", "lukis", "tari", "desain", "kreatif"], value: "Seni" },
  { keys: ["sains", "science", "ipa", "olimpiade"], value: "Sains" },
  { keys: ["tahfidz", "quran", "qur'an", "mengaji", "hafalan"], value: "Tahfidz" },
  { keys: ["bahasa", "english", "inggris", "bilingual"], value: "Bahasa" },
];

const clamp = (n, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, Math.round(n)));

// Parse a budget amount (monthly, in Rupiah) from free text.
export function parseBudget(text) {
  if (!text) return null;
  const t = text.toLowerCase();
  
  const cleanT = t.replace(/\s+/g, "");
  
  const jutaMatch = cleanT.match(/(\d+(?:[.,]\d+)?)(?:juta|jt)/);
  if (jutaMatch) {
    const num = parseFloat(jutaMatch[1].replace(",", "."));
    return Math.round(num * 1_000_000);
  }

  const rpMatch = cleanT.match(/rp([\d.]+)/);
  if (rpMatch) {
    const num = parseInt(rpMatch[1].replace(/\./g, ""), 10);
    if (!isNaN(num) && num > 1000) return num;
  }

  const numberMatches = cleanT.match(/[\d.]+/g);
  if (numberMatches) {
    for (const match of numberMatches) {
      const num = parseInt(match.replace(/\./g, ""), 10);
      if (!isNaN(num) && num > 100000) {
         return num;
      }
    }
  }

  return null;
}

// Convert free text + optional structured controls into a criteria object.
export function parseCriteria(text = "", controls = {}) {
  const t = (text || "").toLowerCase();

  // City
  let city = controls.city || null;
  if (!city) {
    city = CITIES.find((c) => t.includes(c.toLowerCase())) || null;
  }

  // Level
  let level = controls.level || null;
  if (!level) {
    const levelMatch = t.match(/\b(tk|paud|sd|smp|sma)\b/);
    if (levelMatch) level = levelMatch[1].toUpperCase() === "PAUD" ? "TK" : levelMatch[1].toUpperCase();
  }

  // Curriculum
  let curriculum = controls.curriculum || null;
  if (!curriculum) {
    for (const c of CURRICULUM_KEYWORDS) {
      if (c.keys.some((k) => t.includes(k))) {
        curriculum = c.value;
        break;
      }
    }
  }

  // Interests
  const interestsSet = new Set(controls.interests || []);
  for (const i of INTEREST_KEYWORDS) {
    if (i.keys.some((k) => t.includes(k))) interestsSet.add(i.value);
  }
  const interests = Array.from(interestsSet);

  // Budget
  const budget = controls.budget || parseBudget(text);

  // Special needs
  const specialNeeds = controls.specialNeeds !== undefined 
    ? controls.specialNeeds 
    : /inklusi|inklusif|berkebutuhan khusus|abk|difabel/.test(t);

  // Specific facility interest (e.g. swimming pool)
  const wantsPool = /renang|berenang|kolam/.test(t);

  return { city, level, curriculum, interests, budget, specialNeeds, wantsPool, rawText: text };
}

// ---- Individual factor scorers (each returns 0..100) ----

function scoreBudget(school, c) {
  if (!c.budget) return 75;
  const t = school.monthlyTuition;
  if (t <= c.budget) {
    const headroom = 1 - t / c.budget; // 0..1
    return clamp(88 + headroom * 12);
  }
  const over = t / c.budget - 1;
  return clamp(80 - over * 130, 15, 100);
}

function scoreLocation(school, c) {
  if (!c.city) {
    return clamp(98 - school.distanceKm * 3, 55, 96);
  }
  let base = c.city === school.city ? 92 : 42;
  const dAdj = Math.max(-10, Math.min(8, 5 - school.distanceKm));
  return clamp(base + dAdj);
}

function scoreCurriculum(school, c) {
  const hasIslam =
    school.religiousPrograms.includes("Islam Terpadu") || school.curriculum === "Islam Terpadu";
  if (c.curriculum) {
    if (school.curriculum === c.curriculum) return 96;
    if (c.curriculum === "Islam Terpadu" && hasIslam) return 94;
    // related national families
    const nasFam = ["Nasional", "Nasional+"];
    if (nasFam.includes(c.curriculum) && nasFam.includes(school.curriculum)) return 78;
    return 55;
  }
  return 75;
}

function scoreInterest(school, c) {
  if (!c.interests.length) return 75;
  const matched = c.interests.filter((i) => school.interests.includes(i)).length;
  return clamp(48 + (matched / c.interests.length) * 52);
}

function scoreFacilities(school, c) {
  let s = clamp(52 + school.facilities.length * 7, 52, 100);
  if (c.wantsPool && !school.facilities.includes("Kolam Renang")) s -= 15;
  return clamp(s);
}

function scoreRatio(school) {
  return clamp(100 - (school.studentTeacherRatio - 8) * 4, 55, 100);
}

function scoreSpecialNeeds(school, c) {
  if (!c.specialNeeds) return 80;
  return school.specialNeedsSupport ? 100 : 30;
}

// Compute the full match for one school.
export function computeMatch(school, criteria) {
  const breakdown = {
    budget: scoreBudget(school, criteria),
    location: scoreLocation(school, criteria),
    curriculum: scoreCurriculum(school, criteria),
    interest: scoreInterest(school, criteria),
    facilities: scoreFacilities(school, criteria),
    ratio: scoreRatio(school),
    specialNeeds: scoreSpecialNeeds(school, criteria),
  };

  const score = clamp(
    breakdown.budget * WEIGHTS.budget +
      breakdown.location * WEIGHTS.location +
      breakdown.curriculum * WEIGHTS.curriculum +
      breakdown.interest * WEIGHTS.interest +
      breakdown.facilities * WEIGHTS.facilities +
      breakdown.ratio * WEIGHTS.ratio +
      breakdown.specialNeeds * WEIGHTS.specialNeeds
  );

  const reasons = buildReasons(school, criteria, breakdown);
  const tradeoffs = buildTradeoffs(school, criteria, breakdown);

  return { score, breakdown, reasons, tradeoffs };
}

function buildReasons(school, c, b) {
  const r = [];
  if (c.budget && school.monthlyTuition <= c.budget) r.push("Budget sesuai");
  if (b.curriculum >= 90) r.push("Kurikulum sesuai");
  const matchedInterests = c.interests.filter((i) => school.interests.includes(i));
  matchedInterests.forEach((i) => r.push(`${i} tersedia`));
  if (c.city && c.city === school.city) r.push("Lokasi sesuai");
  if (c.specialNeeds && school.specialNeedsSupport) r.push("Dukungan inklusif tersedia");
  if (b.ratio >= 90) r.push("Kelas berukuran kecil");
  if (!r.length) r.push("Sekolah dengan reputasi baik");
  return r.slice(0, 5);
}

function buildTradeoffs(school, c, b) {
  const t = [];
  if (c.budget && school.monthlyTuition > c.budget) t.push("Biaya sedikit di atas budget");
  if (c.city && c.city !== school.city) t.push(`Berada di ${school.city}, bukan ${c.city}`);
  if (c.curriculum && b.curriculum < 70) t.push(`Kurikulum ${school.curriculum}, bukan ${c.curriculum}`);
  if (c.wantsPool && !school.facilities.includes("Kolam Renang"))
    t.push("Tidak memiliki fasilitas renang");
  const unmet = c.interests.filter((i) => !school.interests.includes(i));
  if (unmet.length) t.push(`Belum ada program ${unmet[0]}`);
  if (c.specialNeeds && !school.specialNeedsSupport) t.push("Belum ada program inklusif");
  return t.slice(0, 2);
}

// Rank all schools against the criteria and return the top N with match data.
export function runMatch(schools, text, controls = {}, topN = 3) {
  const criteria = parseCriteria(text, controls);
  const ranked = schools
    .map((school) => ({ school, ...computeMatch(school, criteria) }))
    .sort((a, b) => b.score - a.score);
  return { criteria, results: ranked.slice(0, topN), all: ranked };
}
